'use client';

import dayjs from 'dayjs';
import useSWR, { type SWRConfiguration } from 'swr';

/**
 * Portfolio Value K 线（aipollo NexusData API，经本项目代理）
 * GET /api/polymarket/portfolio/value-kline → https://aipollo.io/api/v1/polymarket/portfolio/value-kline
 * 文档：https://aipollo.io/docs（OpenAPI: /api/openapi.json）
 */

export type ValueKlineInterval = '24h' | '1w' | '1m' | '1y' | 'max';

export type TimeRange = '1D' | '1W' | '1M' | '1Y' | 'ALL';

export const timeRanges: TimeRange[] = ['1D', '1W', '1M', '1Y', 'ALL'];

/** API 不支持 YTD，退化为 1y */
export const TIME_RANGE_INTERVAL_MAP: Record<TimeRange, ValueKlineInterval> = {
  '1D': '24h',
  '1W': '1w',
  '1M': '1m',
  '1Y': '1y',
  ALL: 'max',
};

/**
 * K 线点：timestamp 为快照时刻字符串（日表 yyyymmdd / 小时表 yyyymmddHH）；
 * current_value = position_value（持仓价值）+ usdc_value（钱包稳定币余额）
 */
export interface ValueKlineRawPoint {
  timestamp: string;
  current_value: number;
  position_value: number;
  usdc_value: number;
}

export interface ValueKlineData {
  interval: string;
  /** epoch 秒 */
  start_time: number;
  /** epoch 秒 */
  end_time: number;
  history?: ValueKlineRawPoint[];
}

export interface ValueKlineMeta {
  as_of: string;
  source_tables: string[];
  tier_applied: string;
  cache_ttl: number;
  preview_applied: boolean;
  warnings?: string[];
}

export interface ValueKlineResponse {
  data: ValueKlineData;
  meta: ValueKlineMeta;
}

/** 图表用数据点（timestamp 已转为 epoch 毫秒，UTC） */
export interface ValueKlineChartPoint {
  timestamp: number;
  value: number;
  positionValue: number;
  usdcValue: number;
}

export interface FetchValueKlineParams {
  /** 钱包地址，最多 10 个；上游仅支持单地址，多钱包并发请求后按时间戳求和合并 */
  wallets: string[];
  /** 默认 '24h' */
  interval?: ValueKlineInterval;
}

export interface UseValueKlineOptions extends FetchValueKlineParams {
  enabled?: boolean;
  swr?: SWRConfiguration<ValueKlineResponse, Error>;
}

const VALUE_KLINE_ENDPOINT = '/api/polymarket/portfolio/value-kline';
const DEFAULT_INTERVAL: ValueKlineInterval = '24h';
const MAX_WALLETS = 10;

export async function fetchValueKline({
  wallets,
  interval = DEFAULT_INTERVAL,
}: FetchValueKlineParams): Promise<ValueKlineResponse> {
  const normalizedWallets = normalizeWallets(wallets);
  if (normalizedWallets.length === 0) {
    throw new Error('wallets is required');
  }

  // 上游仅支持单地址查询，多钱包并发请求后按时间戳求和合并
  const responses = await Promise.all(
    normalizedWallets.map((wallet) => fetchValueKlineForWallet(wallet, interval)),
  );
  return mergeValueKlineResponses(responses);
}

async function fetchValueKlineForWallet(
  wallet: string,
  interval: ValueKlineInterval,
): Promise<ValueKlineResponse> {
  const searchParams = new URLSearchParams({ wallets: wallet, interval });
  const response = await fetch(`${VALUE_KLINE_ENDPOINT}?${searchParams.toString()}`, {
    headers: { accept: 'application/json' },
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch portfolio value kline: ${response.status}`);
  }

  const result = (await response.json()) as ValueKlineResponse & {
    error?: { code?: string; message?: string };
  };

  if (result.error) {
    throw new Error(result.error.message || result.error.code || 'Failed to fetch value kline');
  }

  return result;
}

/** 多钱包曲线按 timestamp 对齐求和；某钱包缺失的时间点按 0 处理（直接累加已有值） */
function mergeValueKlineResponses(responses: ValueKlineResponse[]): ValueKlineResponse {
  if (responses.length === 1) return responses[0];

  const pointByTimestamp = new Map<string, ValueKlineRawPoint>();

  for (const response of responses) {
    for (const point of response.data?.history ?? []) {
      const existing = pointByTimestamp.get(point.timestamp);
      if (existing) {
        existing.current_value += point.current_value || 0;
        existing.position_value += point.position_value || 0;
        existing.usdc_value += point.usdc_value || 0;
      } else {
        pointByTimestamp.set(point.timestamp, { ...point });
      }
    }
  }

  const history = Array.from(pointByTimestamp.values()).sort((a, b) =>
    a.timestamp.localeCompare(b.timestamp),
  );

  return {
    data: {
      interval: responses[0].data?.interval ?? DEFAULT_INTERVAL,
      start_time: Math.min(...responses.map((r) => r.data?.start_time ?? Infinity)),
      end_time: Math.max(...responses.map((r) => r.data?.end_time ?? -Infinity)),
      history,
    },
    meta: responses[0].meta,
  };
}

export function useValueKline(options: UseValueKlineOptions) {
  const wallets = normalizeWallets(options.wallets);
  const interval = options.interval ?? DEFAULT_INTERVAL;
  const enabled = options.enabled ?? true;
  const swrKey =
    enabled && wallets.length > 0 ? ['value-kline', wallets.join(','), interval] : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<ValueKlineResponse, Error>(
    swrKey,
    () => fetchValueKline({ wallets, interval }),
    {
      keepPreviousData: true,
      ...options.swr,
    },
  );

  const history = data?.data?.history ?? [];

  return {
    data: data ?? null,
    history,
    points: toChartPoints(history),
    meta: data?.meta ?? null,
    interval,
    wallets,
    error,
    isLoading,
    isValidating,
    refresh: mutate,
  };
}

/** 原始点 → 图表点：yyyymmdd / yyyymmddHH 字符串解析为 UTC epoch 毫秒，按时间升序 */
export function toChartPoints(history: ValueKlineRawPoint[]): ValueKlineChartPoint[] {
  return history
    .map((point) => ({
      timestamp: dayjs(point.timestamp).valueOf(),
      value: point.current_value,
      positionValue: point.position_value,
      usdcValue: point.usdc_value,
    }))
    .filter((point) => Number.isFinite(point.timestamp))
    .sort((a, b) => a.timestamp - b.timestamp);
}

function parseKlineTimestamp(value: string): number {
  if (!/^\d{8}(\d{2})?$/.test(value)) return NaN;
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(4, 6));
  const day = Number(value.slice(6, 8));
  const hour = value.length === 10 ? Number(value.slice(8, 10)) : 0;
  return Date.UTC(year, month - 1, day, hour);
}

function normalizeWallets(wallets: string[]) {
  return [...new Set(wallets.map((wallet) => wallet.trim().toLowerCase()).filter(Boolean))].slice(
    0,
    MAX_WALLETS,
  );
}

export default useValueKline;
